import { useState, useEffect } from 'react';
import * as services from '../services/inventoryService';

export const useSaleEntryForm = (initialData = null) => {
  const [form, setForm] = useState({
    saleType: "",
    invoiceNo: "Auto-generated",
    saleDate: new Date().toISOString().split('T')[0],
    customer: "",
    customerPhone: "",
    reference: "",
    customerEmail: "",
    billingAddressLine1: "",
    billingAddressLine2: "",
    billingCity: "",
    billingState: "",
    billingPincode: "",
    deliveryContactPerson: "",
    deliveryPhone: "",
    deliveryAddressLine1: "",
    deliveryAddressLine2: "",
    deliveryCity: "",
    deliveryState: "",
    deliveryPincode: "",
    courierName: "",
    trackingNumber: "",
    deliveryDate: "",
    deliveryStatus: "pending",
    deliveryNotes: "",
    remarks: "",
    discountPercent: 0,
    discountAmount: 0,
    taxPercent: 0,
    roundOff: undefined,
  });

  const [errors, setErrors] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(false);

  const fetchInvoiceNo = async () => {
    try {
      const res = await services.fetchNextInvoiceNo();
      if (res.data && res.data.nextInvoiceNo) {
        setForm(prev => ({ ...prev, invoiceNo: res.data.nextInvoiceNo }));
      }
    } catch (err) {
      console.error("Failed to fetch next invoice no", err);
    }
  };

  useEffect(() => {
    if (initialData) {
      setForm(prev => ({ ...prev, ...initialData }));
    } else {
      fetchInvoiceNo();
    }
  }, [initialData]);

  useEffect(() => {
    if (sameAsBilling) {
      setForm((prev) => ({
        ...prev,
        deliveryAddressLine1: prev.billingAddressLine1,
        deliveryAddressLine2: prev.billingAddressLine2,
        deliveryCity: prev.billingCity,
        deliveryState: prev.billingState,
        deliveryPincode: prev.billingPincode,
      }));
      // Clear errors for delivery fields since they match billing which should be validated
      setErrors(prev => ({
        ...prev,
        deliveryPincode: prev.billingPincode ? validateField('deliveryPincode', prev.billingPincode) : ""
      }));
    }
  }, [
    sameAsBilling,
    form.billingAddressLine1,
    form.billingAddressLine2,
    form.billingCity,
    form.billingState,
    form.billingPincode,
  ]);

  const validateField = (name, value) => {
    let error = "";
    if (name === "customerPhone" || name === "deliveryPhone") {
      if (value && !/^\d{10}$/.test(value)) {
        error = "Please enter exactly 10 digits for the phone number.";
      }
    } else if (name === "customerEmail") {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = "Please include an '@' and a valid domain (e.g. name@example.com).";
      }
    } else if (name === "billingPincode" || name === "deliveryPincode") {
      if (value && !/^\d{6}$/.test(value)) {
        error = "Pincode must be exactly 6 digits long.";
      }
    }
    return error;
  };

  const updateField = (key, value) => {
    let sanitizedValue = value;

    if (key === "customerPhone" || key === "deliveryPhone") {
      sanitizedValue = value.replace(/\D/g, "");
      if (sanitizedValue.length > 10) return;
    }
    
    if (key === "billingPincode" || key === "deliveryPincode") {
      sanitizedValue = value.replace(/\D/g, "");
      if (sanitizedValue.length > 6) return;
    }

    setForm(prev => ({ ...prev, [key]: sanitizedValue }));
    const error = validateField(key, sanitizedValue);
    if (error) {
      setErrors(prev => ({ ...prev, [key]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    const requiredFields = ['customer', 'customerPhone', 'saleType'];

    Object.keys(form).forEach(key => {
      let error = validateField(key, form[key]);
      
      if (requiredFields.includes(key) && (!form[key] || String(form[key]).trim() === '')) {
        error = "This field is required";
      }

      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setForm({
      saleType: "",
      invoiceNo: "Auto-generated",
      saleDate: new Date().toISOString().split('T')[0],
      customer: "",
      customerPhone: "",
      reference: "",
      customerEmail: "",
      billingAddressLine1: "",
      billingAddressLine2: "",
      billingCity: "",
      billingState: "",
      billingPincode: "",
      deliveryContactPerson: "",
      deliveryPhone: "",
      deliveryAddressLine1: "",
      deliveryAddressLine2: "",
      deliveryCity: "",
      deliveryState: "",
      deliveryPincode: "",
      courierName: "",
      trackingNumber: "",
      deliveryDate: "",
      deliveryStatus: "pending",
      deliveryNotes: "",
      remarks: "",
      discountPercent: 0,
      discountAmount: 0,
      taxPercent: 0,
      roundOff: undefined,
    });
    setErrors({});
    setSameAsBilling(false);
    fetchInvoiceNo();
  };

  return {
    form,
    errors,
    sameAsBilling,
    setSameAsBilling,
    updateField,
    validateForm,
    resetForm
  };
};
